/**
 * Modal de **alta/edición de usuario** (`POST /users` / `PATCH /users/<username>`).
 *
 * Réplica de `add_user.html`/`edit_user.html` del SSR (`progress/
 * explore_equipo_ssr.md`, §B.2/B.3): username, nombre, contraseña, rol (roles del
 * sistema + personalizados en optgroup) y sede (select solo para el admin
 * multi-sede; el resto opera en su propia sede). Valida con Zod antes de delegar y
 * traduce el error de la API a mensaje legible.
 *
 * Controlado por el padre (`open`). En `create` la contraseña es obligatoria; en
 * `edit` es opcional (vacía = no cambiar). Presentacional + hooks; sin `fetch`.
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";
import { useSedes } from "@/features/auth";
import { useAuth } from "@/lib/auth";

import { teamErrorMessage } from "../format";
import { useCreateUser, useUpdateUser } from "../hooks/useUserMutations";
import { useRoles } from "../hooks/useUsers";
import {
  createUserSchema,
  editUserSchema,
  type CreateUserValues,
  type EditUserValues,
} from "../schemas";
import type { CreateUserBody, UpdateUserBody, UserListItem } from "../types";

/** Estilo de un `<select>` coherente con `components/ui/Input`. */
const SELECT_CLASS =
  "block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-red-500";

interface UserFormDialogProps {
  /** `null` mantiene el modal cerrado. En `edit`, el usuario a editar. */
  target: { mode: "create" } | { mode: "edit"; user: UserListItem } | null;
  onClose: () => void;
}

/** Errores por campo (de Zod). */
type FieldErrors = Partial<
  Record<"username" | "name" | "password" | "role" | "sede_id", string>
>;

interface FormState {
  username: string;
  name: string;
  password: string;
  role: string;
  sede_id: string;
}

const EMPTY_STATE: FormState = {
  username: "",
  name: "",
  password: "",
  role: "",
  sede_id: "",
};

export function UserFormDialog({ target, onClose }: UserFormDialogProps) {
  const { isMultiSede, user: currentUser } = useAuth();
  const roles = useRoles();
  // El select de sede solo se ofrece al admin multi-sede (igual que el SSR); el
  // resto opera en su propia sede, así que no se piden las sedes.
  const sedes = useSedes(isMultiSede && target !== null);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isEdit = target?.mode === "edit";

  // Al abrir/cambiar de objetivo, hidrata el formulario y limpia el estado de
  // mutación (igual que el modal de asignación de talonario).
  useEffect(() => {
    if (target === null) {
      return;
    }
    if (target.mode === "edit") {
      setForm({
        username: target.user.username,
        name: target.user.name,
        password: "",
        role: target.user.role,
        sede_id: target.user.sede_id != null ? String(target.user.sede_id) : "",
      });
    } else {
      setForm({
        ...EMPTY_STATE,
        // En alta, por defecto la sede del actor (como el SSR cuando no la elige).
        sede_id: currentUser?.sede_id != null ? String(currentUser.sede_id) : "",
      });
    }
    setFieldErrors({});
    createUser.reset();
    updateUser.reset();
    // Solo nos interesa reaccionar al cambio de objetivo (las mutaciones son
    // estables por render de TanStack Query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  function setField(key: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function collectErrors(
    fieldErrorsObj: Partial<Record<keyof FormState, string[] | undefined>>,
  ): FieldErrors {
    const errors: FieldErrors = {};
    (Object.keys(fieldErrorsObj) as (keyof FormState)[]).forEach((key) => {
      const message = fieldErrorsObj[key]?.[0];
      if (message) {
        errors[key] = message;
      }
    });
    return errors;
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (target === null) {
      return;
    }

    if (target.mode === "create") {
      const result = createUserSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitCreate(result.data);
    } else {
      const result = editUserSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitEdit(target.user.username, result.data);
    }
  }

  function submitCreate(values: CreateUserValues) {
    const body: CreateUserBody = {
      username: values.username,
      name: values.name,
      password: values.password,
      role: values.role,
      sede_id: Number(values.sede_id),
    };
    createUser.mutate(body, { onSuccess: onClose });
  }

  function submitEdit(originalUsername: string, values: EditUserValues) {
    const body: UpdateUserBody = {
      username: values.username,
      name: values.name,
      role: values.role,
      sede_id: Number(values.sede_id),
    };
    // La contraseña solo viaja si se escribió una nueva (PATCH parcial del backend).
    if (values.password && values.password.length > 0) {
      body.password = values.password;
    }
    updateUser.mutate({ username: originalUsername, body }, { onSuccess: onClose });
  }

  const activeMutation = isEdit ? updateUser : createUser;
  const apiError = activeMutation.isError
    ? teamErrorMessage(activeMutation.error)
    : null;
  const isSubmitting = activeMutation.isPending;

  const systemRoles = roles.data?.system ?? [];
  const customRoles = roles.data?.custom ?? [];

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>{isEdit ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Modifica los datos del usuario. Deja la contraseña en blanco para conservarla."
            : "Completa los datos para crear un nuevo usuario."}
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="user-username" className="text-sm font-medium text-slate-700">
              Usuario
            </label>
            <Input
              id="user-username"
              autoComplete="off"
              value={form.username}
              aria-invalid={fieldErrors.username ? true : undefined}
              aria-describedby={fieldErrors.username ? "user-username-error" : undefined}
              onChange={(event) => setField("username", event.target.value)}
            />
            {fieldErrors.username && (
              <p id="user-username-error" className="text-sm text-red-600">
                {fieldErrors.username}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="user-name" className="text-sm font-medium text-slate-700">
              Nombre
            </label>
            <Input
              id="user-name"
              autoComplete="off"
              value={form.name}
              aria-invalid={fieldErrors.name ? true : undefined}
              aria-describedby={fieldErrors.name ? "user-name-error" : undefined}
              onChange={(event) => setField("name", event.target.value)}
            />
            {fieldErrors.name && (
              <p id="user-name-error" className="text-sm text-red-600">
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="user-password" className="text-sm font-medium text-slate-700">
              Contraseña{isEdit ? " (opcional)" : ""}
            </label>
            <Input
              id="user-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : undefined}
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? "user-password-error" : undefined}
              onChange={(event) => setField("password", event.target.value)}
            />
            {fieldErrors.password && (
              <p id="user-password-error" className="text-sm text-red-600">
                {fieldErrors.password}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="user-role" className="text-sm font-medium text-slate-700">
              Rol
            </label>
            <select
              id="user-role"
              className={SELECT_CLASS}
              value={form.role}
              aria-invalid={fieldErrors.role ? true : undefined}
              aria-describedby={fieldErrors.role ? "user-role-error" : undefined}
              onChange={(event) => setField("role", event.target.value)}
            >
              <option value="">Selecciona un rol…</option>
              <optgroup label="Roles del sistema">
                {systemRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </optgroup>
              {customRoles.length > 0 && (
                <optgroup label="Roles personalizados">
                  {customRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {fieldErrors.role && (
              <p id="user-role-error" className="text-sm text-red-600">
                {fieldErrors.role}
              </p>
            )}
          </div>

          {isMultiSede && (
            <div className="flex flex-col gap-1">
              <label htmlFor="user-sede" className="text-sm font-medium text-slate-700">
                Sede
              </label>
              <select
                id="user-sede"
                className={SELECT_CLASS}
                value={form.sede_id}
                aria-invalid={fieldErrors.sede_id ? true : undefined}
                aria-describedby={fieldErrors.sede_id ? "user-sede-error" : undefined}
                onChange={(event) => setField("sede_id", event.target.value)}
              >
                <option value="">Selecciona una sede…</option>
                {(sedes.data ?? []).map((sede) => (
                  <option key={sede.id} value={sede.id}>
                    {sede.nombre}
                  </option>
                ))}
              </select>
              {fieldErrors.sede_id && (
                <p id="user-sede-error" className="text-sm text-red-600">
                  {fieldErrors.sede_id}
                </p>
              )}
            </div>
          )}

          {apiError && (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Guardando…" : isEdit ? "Actualizar" : "Crear usuario"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
